import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2, UploadCloud } from "lucide-react";
import api from "../../../lib/api";

// Product image upload (Sep 2026) — Dashboard -> Retail Catalog ->
// Edit Product.
//
// The tenant photographs a dish and it appears on their storefront. No
// external host, no Google Drive, no pasted URL, no Cloudflare account,
// and no idea that a bucket exists anywhere.
//
// WHY THIS UPLOADS IMMEDIATELY RATHER THAN ON SAVE
// ------------------------------------------------
// The image is a system-adjacent concern with its own endpoints, exactly
// like the parcel-shipping block in this same modal (which posts to
// `PUT /commerce/parcel-shipping/products/{id}`) and the tenant logo
// (`LogoUploader` -> `POST /branding/logo`). Staging the file until Save
// would mean holding several megabytes in browser memory across the
// modal's lifetime, a temporary object in storage, and a promotion step
// — three new failure modes to avoid one edge case.
//
// The cost is stated plainly to the tenant in the hint text below: the
// photo is saved as soon as it uploads, separately from Save.
//
// This component owns ONLY its own state. A failed upload never touches
// the product form's `draft`, so nothing the tenant typed is lost — the
// modal is never reset or reloaded by anything that happens in here.

const ACCEPT = "image/jpeg,image/png,image/webp";
const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024; // mirrors services/product_images.MAX_SOURCE_BYTES

// The backend is the authority on every rule below; these client checks
// exist purely so the common mistakes are caught instantly instead of
// after a 5 MB round trip. A file that slips past them is still rejected
// server-side, and that message is what gets shown.
function clientSideReason(file) {
  if (!file) return "";
  if (!ACCEPTED_TYPES.includes((file.type || "").toLowerCase())) {
    return "Product photos must be a JPEG, PNG or WebP image.";
  }
  if (file.size === 0) return "That file is empty. Choose a photo and try again.";
  if (file.size > MAX_BYTES) {
    return `That photo is ${(file.size / (1024 * 1024)).toFixed(1)} MB. Product photos must be under 5 MB — try a smaller photo.`;
  }
  return "";
}

export default function ProductImageUploader({
  catalogId,
  productId,
  imageUrl,
  onChange,
  testIdPrefix = "product-image",
}) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState("");
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [previewBroken, setPreviewBroken] = useState(false);
  // Which operation `busy` belongs to, so the progress text never says
  // "Uploading" while a photo is being removed.
  const [busyAction, setBusyAction] = useState("");
  const inputRef = useRef(null);
  const previewRef = useRef("");
  // Synchronous in-flight guard. `busy` is React state, so two drops or
  // file selections fired before the next render would both still read
  // `busy === false` and start two uploads. A ref flips immediately.
  const inFlightRef = useRef(false);

  // Revoke the object URL we created, never one we were handed. Leaking
  // these keeps the decoded bitmap alive for the life of the tab.
  const clearPreview = useCallback(() => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = "";
    }
    setPreview("");
  }, []);

  useEffect(() => () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
  }, []);

  const shown = preview || imageUrl || "";
  const hasImage = Boolean(imageUrl);

  // A broken image is tracked in state (never by mutating the <img>'s
  // style), and reset whenever the source changes — so a dead legacy URL
  // can never leave the NEXT photo, or its local preview, invisible.
  useEffect(() => { setPreviewBroken(false); }, [shown]);

  const upload = useCallback(async (file) => {
    // Double-submit guard. `busy` also disables every control, but a
    // drop event can still fire on a disabled container in some
    // browsers, and two events can land before React re-renders — so
    // the authoritative guard is the synchronous ref, not the state.
    if (!file || inFlightRef.current) return;

    const reason = clientSideReason(file);
    if (reason) {
      setErr(reason);
      return;
    }
    inFlightRef.current = true;

    setErr("");
    setConfirmingRemove(false);
    clearPreview();
    const localUrl = URL.createObjectURL(file);
    previewRef.current = localUrl;
    setPreview(localUrl);
    setBusy(true);
    setBusyAction("upload");
    setProgress(0);

    const form = new FormData();
    form.append("file", file);

    try {
      const { data } = await api.post(
        `/retail/catalogs/${catalogId}/products/${productId}/image`,
        form,
        {
          onUploadProgress: (e) => {
            if (!e || !e.total) return;
            setProgress(Math.round((e.loaded * 100) / e.total));
          },
        },
      );
      clearPreview();
      onChange?.(data?.image?.image_url || "");
    } catch (e) {
      // Show the server's own message when it sent one — it is written
      // for a restaurant owner and names the fix. Never surface a raw
      // exception or status code.
      const d = e?.response?.data?.detail;
      setErr(d?.message || d?.error || "That photo could not be uploaded. Please try again.");
      clearPreview();
    } finally {
      inFlightRef.current = false;
      setBusy(false);
      setBusyAction("");
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [catalogId, productId, onChange, clearPreview]);

  const remove = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    setBusyAction("remove");
    setErr("");
    try {
      await api.delete(`/retail/catalogs/${catalogId}/products/${productId}/image`);
      clearPreview();
      onChange?.("");
      setConfirmingRemove(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d?.message || d?.error || "That photo could not be removed. Please try again.");
    } finally {
      inFlightRef.current = false;
      setBusy(false);
      setBusyAction("");
    }
  }, [catalogId, productId, onChange, clearPreview]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (inFlightRef.current) return;
    upload(e.dataTransfer?.files?.[0]);
  };

  return (
    <div
      className="pt-3 border-t border-slate-200"
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
        Product photo
      </div>

      <div className="flex items-start gap-3">
        {/* Preview / placeholder. One fixed box so the row never jumps
            between the two states. */}
        <div
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={
            "relative shrink-0 w-24 h-24 rounded-lg border grid place-items-center overflow-hidden bg-slate-50 "
            + (dragging ? "border-emerald-500 border-2 bg-emerald-50" : "border-dashed border-slate-300")
          }
          data-testid={`${testIdPrefix}-dropzone`}
        >
          {shown && !previewBroken ? (
            <img
              key={shown}
              src={shown}
              alt=""
              className="w-full h-full object-cover"
              data-testid={`${testIdPrefix}-preview`}
              onError={() => setPreviewBroken(true)}
            />
          ) : (
            <div
              className="flex flex-col items-center gap-1 text-slate-400"
              data-testid={`${testIdPrefix}-placeholder`}
            >
              <ImagePlus className="w-6 h-6" />
              <span className="text-[10px]">No photo</span>
            </div>
          )}

          {busy && (
            <div
              className="absolute inset-0 bg-white/80 grid place-items-center"
              data-testid={`${testIdPrefix}-uploading`}
            >
              <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <input
            ref={inputRef}
            id={`${testIdPrefix}-file`}
            type="file"
            accept={ACCEPT}
            disabled={busy}
            className="sr-only"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`${testIdPrefix}-input`}
          />

          <div className="flex flex-wrap items-center gap-2">
            <label
              htmlFor={`${testIdPrefix}-file`}
              className={
                "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm "
                + (busy
                  ? "border-slate-200 text-slate-400 cursor-not-allowed"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer")
              }
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              <UploadCloud className="w-4 h-4" />
              {hasImage ? "Replace photo" : "Upload photo"}
            </label>

            {hasImage && !confirmingRemove && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmingRemove(true)}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                data-testid={`${testIdPrefix}-remove-btn`}
              >
                <Trash2 className="w-4 h-4" />
                Remove photo
              </button>
            )}

            {/* Deliberate two-step removal. Losing a photo to a stray
                click is a real cost to a tenant who took it themselves. */}
            {confirmingRemove && (
              <span className="inline-flex items-center gap-2" data-testid={`${testIdPrefix}-confirm-remove`}>
                <span className="text-xs text-slate-600">Remove this product photo?</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={remove}
                  className="h-9 px-3 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-remove-confirm-btn`}
                >
                  {busyAction === "remove" ? "Removing…" : "Remove photo"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirmingRemove(false)}
                  className="h-9 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-remove-cancel-btn`}
                >
                  Cancel
                </button>
              </span>
            )}
          </div>

          {busy && busyAction === "remove" && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid={`${testIdPrefix}-removing`}>
              Removing photo…
            </div>
          )}

          {busy && busyAction !== "remove" && (
            <div className="mt-2" data-testid={`${testIdPrefix}-progress`}>
              <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full bg-emerald-600 transition-[width]"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Uploading… {progress}%
              </div>
            </div>
          )}

          {err ? (
            <div
              className="mt-2 text-xs text-rose-700"
              role="alert"
              data-testid={`${testIdPrefix}-error`}
            >
              {err}
            </div>
          ) : (
            <div className="mt-2 text-[11px] text-slate-500">
              JPEG, PNG or WebP, up to 5 MB. The photo is saved as soon as it
              uploads — you do not need to press Save for it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
